import { api } from './client'

export interface ModifyRequest {
  qboCompanyId: string | null
  entityType: string
  reference?: string
  dateMode: 'preset' | 'custom'
  preset?: string
  from?: string
  to?: string
}

export interface ModifyPreviewRequest extends ModifyRequest {
  qboCompanyId: string
  page?: number
  pageSize?: number
}

/** One matching record: the fields the grid renders, plus what a write needs. */
export interface ModifyPreviewRow {
  id: string
  syncToken: string | null
  fields: Record<string, unknown>
}

export interface ModifyPreviewResponse {
  records: ModifyPreviewRow[]
  page: number
  pageSize: number
  totalCount: number | null
  /** "DisplayName" or "DocNumber" — the field the reference box filters on. */
  referenceField: string
  entityType: string
  /**
   * The template's column labels in template order — matching Export's columns.
   * Null when no template exists for this entity.
   */
  exportColumns: string[] | null
}

/** One edited record: only the fields the user actually changed. */
export interface ModifyEdit {
  id: string
  syncToken: string | null
  fields: Record<string, string | null>
}

export interface ModifySubmitRequest {
  qboCompanyId: string
  entityType: string
  edits: ModifyEdit[]
}

/**
 * `POST /api/modify/preview` runs the same query an export would (read-only,
 * never persists) and returns one page of real records — plus each record's id
 * and SyncToken, which the editable grid needs to write the changes back.
 *
 * `POST /api/modify/submit` queues a real modify job and returns `{ jobId }`.
 * Nothing is written synchronously: the worker sends each edit to QuickBooks one
 * at a time. Only the changed fields are sent, so everything else on a record is
 * left exactly as it is.
 */
export const modifyApi = {
  preview(request: ModifyPreviewRequest): Promise<ModifyPreviewResponse> {
    return api.post<ModifyPreviewResponse>('/modify/preview', request)
  },

  submit(request: ModifySubmitRequest): Promise<{ jobId: string }> {
    return api.post<{ jobId: string }>('/modify/submit', request)
  },
}
